// /echoes — the lore-facing surface for narrative quests the bond can
// walk. Each Echo is a fragment of pre-Shattering time the Ember leads
// the user through. Internal code keeps "quest" naming for lower
// friction (routes, schemas, persistence touchpoints all stay
// `quest:*`); only the user-visible label is "Echo" / "Eco" to keep
// the IP voice and avoid the gamification connotations of "quest".
//
// Phase 1: one Echo (quest 01 — The Listening Stone), discovered via
// this page directly. Phase 2 will add letter-based discovery (the
// Ember invites the user to a new Echo via a letter) and an in-nav
// badge when a freshly-revealed Echo has not been opened.

import type { BondDto } from '@athrelaris/shared';
import { Link, createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QUESTS } from '../data/quests/index.js';
import { switchLocaleToBond } from '../i18n/index.js';
import { getMyBond } from '../lib/api';
import { redirectIfLandingOnly } from '../lib/landing-mode';
import { getSessionSafe } from '../lib/auth-client';
import { useEmberTheme } from '../lib/use-ember-theme';

// Catalog of Echoes available in the build. Manual for now — when the
// second Echo lands, swap this for a derived list from `QUESTS` plus
// per-echo cover/availability metadata.
interface EchoEntry {
  questId: string;
  // Path under /apps/web/public used as the card cover. Picked for
  // legibility in a small grid card (atmospheric, no characters) per
  // the IP's "cover frame: legibility > narrative" rule.
  coverSrc: string;
}

const ECHO_CATALOG: EchoEntry[] = [
  {
    questId: 'quest-01-listening-stone',
    coverSrc: '/quests/quest-01-listening-stone/scenes/stone-and-dawn-grey-light.png',
  },
];

type EchoStatus = 'available' | 'in_progress' | 'completed';

interface PersistedQuestState {
  questId: string;
  currentBeatId: string;
  startedAt: string;
  completedAt: string | null;
}

function readQuestState(questId: string): PersistedQuestState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`athrelaris.quest.${questId}`);
    if (!raw) return null;
    return JSON.parse(raw) as PersistedQuestState;
  } catch {
    return null;
  }
}

function resolveStatus(questId: string, startBeatId: string): EchoStatus {
  const state = readQuestState(questId);
  if (!state) return 'available';
  if (state.completedAt) return 'completed';
  if (state.currentBeatId && state.currentBeatId !== startBeatId) return 'in_progress';
  return 'available';
}

export const Route = createFileRoute('/echoes')({
  beforeLoad: async () => {
    redirectIfLandingOnly();
    const data = await getSessionSafe();
    if (!data?.session) throw redirect({ to: '/login' });
  },
  loader: async () => {
    const bond = await getMyBond();
    if (!bond) throw redirect({ to: '/awakening' });
    return { bond };
  },
  component: EchoesPage,
});

function EchoesPage() {
  const { t } = useTranslation();
  const { bond } = Route.useLoaderData();
  const locale = bond.locale === 'es' ? 'es' : 'en';

  useEmberTheme(bond.emberColorPalette);
  useEffect(() => {
    switchLocaleToBond(bond.locale);
  }, [bond.locale]);

  // Resolve each echo's status on mount. Status is read from
  // localStorage, so a navigate-away-and-back will pick up changes the
  // /quest route made — no polling needed.
  const echoes = useMemo(() => {
    return ECHO_CATALOG.map((entry) => {
      const quest = QUESTS[entry.questId];
      if (!quest) return null;
      return {
        entry,
        quest,
        status: resolveStatus(entry.questId, quest.startBeatId),
      };
    }).filter((x): x is NonNullable<typeof x> => x !== null);
  }, []);

  const isEmpty = echoes.length === 0;

  return (
    <main className="mx-auto flex max-w-2xl flex-col px-4 pt-12 sm:pt-8 lg:pt-4">
      <header className="mb-8 text-center animate-fadeUp lg:mb-10">
        <p className="font-display text-[0.62rem] text-accent-strong uppercase tracking-[0.4em] sm:text-xs lg:text-[0.78rem]">
          {t('echoes.eyebrow')}
        </p>
        <h1
          className="mt-4 font-display text-3xl text-ash-50 tracking-wide sm:text-4xl lg:text-5xl"
          style={{ fontWeight: 500 }}
        >
          {t('echoes.heading')}
        </h1>
        <p className="mt-4 font-body text-ash-300 text-base italic leading-snug sm:text-lg lg:mt-6 lg:text-xl">
          {t('echoes.subheading', { name: bond.emberName })}
        </p>
        <div className="mx-auto mt-7 flex max-w-xs items-center gap-3 lg:mt-9">
          <span className="hairline h-px flex-1" />
          <span aria-hidden="true" className="font-display text-accent text-sm">◇</span>
          <span className="hairline h-px flex-1" />
        </div>
      </header>

      {isEmpty ? (
        <p className="mx-auto max-w-md text-center font-body text-ash-300 italic leading-relaxed lg:text-[1.05rem]">
          {t('echoes.empty', { name: bond.emberName })}
        </p>
      ) : (
        <ul className="space-y-7 lg:space-y-9">
          {echoes.map(({ entry, quest, status }, idx) => (
            <li
              key={entry.questId}
              className="animate-fadeUp"
              style={{ animationDelay: `${0.15 + idx * 0.08}s` }}
            >
              <EchoCard
                questId={entry.questId}
                coverSrc={entry.coverSrc}
                title={quest.title[locale]}
                subtitle={quest.subtitle[locale]}
                status={status}
              />
            </li>
          ))}
        </ul>
      )}

      <footer className="mt-16 border-paper-800/50 border-t pt-8 text-center font-body text-ash-500 text-xs italic lg:mt-20 lg:text-sm">
        <p>{t('echoes.footer', { name: bond.emberName })}</p>
      </footer>
    </main>
  );
}

interface EchoCardProps {
  questId: string;
  coverSrc: string;
  title: string;
  subtitle: string;
  status: EchoStatus;
}

function EchoCard({ questId, coverSrc, title, subtitle, status }: EchoCardProps) {
  const { t } = useTranslation();

  const isCompleted = status === 'completed';
  const isInProgress = status === 'in_progress';

  const statusLabel = isCompleted
    ? t('echoes.statusCompleted')
    : isInProgress
      ? t('echoes.statusInProgress')
      : t('echoes.statusAvailable');

  const ctaLabel = isCompleted
    ? t('echoes.ctaReplay')
    : isInProgress
      ? t('echoes.ctaResume')
      : t('echoes.ctaOpen');

  return (
    <Link
      to="/quest/$questId"
      params={{ questId }}
      className={`group relative block overflow-hidden rounded-sm border-l-2 transition ${
        isCompleted
          ? 'border-ash-700/60 bg-paper-900/20 hover:bg-paper-900/30'
          : 'border-accent bg-paper-900/40 hover:bg-paper-800/50'
      }`}
    >
      <div className="aspect-[16/9] w-full overflow-hidden bg-paper-950">
        <img
          src={coverSrc}
          alt=""
          loading="lazy"
          className={`h-full w-full object-cover transition duration-700 group-hover:scale-[1.02] ${
            isCompleted ? 'opacity-70' : 'opacity-95'
          }`}
        />
      </div>
      <div className="px-5 py-5 sm:px-7 sm:py-6 lg:px-8 lg:py-7">
        <p className="font-display text-[0.58rem] text-accent-strong uppercase tracking-[0.36em] sm:text-[0.62rem]">
          {statusLabel}
        </p>
        <h2
          className="mt-3 font-display text-xl text-ash-50 tracking-wide sm:text-2xl"
          style={{ fontWeight: 500 }}
        >
          {title}
        </h2>
        <p className="mt-3 font-body text-ash-300 text-sm italic leading-relaxed sm:text-base">
          {subtitle}
        </p>
        <p className="mt-5 flex items-center gap-3 font-display text-[0.6rem] text-accent uppercase tracking-[0.34em] sm:text-[0.66rem]">
          <span aria-hidden="true">◇</span>
          <span>{ctaLabel}</span>
        </p>
      </div>
    </Link>
  );
}
